import { Link } from 'react-router-dom';

export default function ApiEndpoints() {
  return (
    <div>
      <p>
        DISGENET REST API provides a set of endpoints to query information on genes and variants associated
        with human diseases. To get a detailed description of the input parameters and the format of the
        response of each REST API endpoint, access the REST API documentation in the{' '}
        <Link to="/interactive-console">Interactive console</Link>
        . From the REST API documentation, by providing your API key, you can easily and interactively query
        all REST API endpoints as well as visually inspect the results.
        <br />
        <br />
        <b>⇒ Exploit your API key in the Interactive console of the REST API:</b>: the{' '}
        <Link to="/interactive-console">Interactive console</Link> enables users to easily and interactively
        query all REST API endpoints as well as visually inspect the results. To this purpose, you need to
        provide your API key by accessing the REST API in the{' '}
        <Link to="/interactive-console">Interactive console</Link> and pasting it into the API key field in
        the console header. From then on all your API calls will be automatically authorized by means of your
        API key.
        <br />
        <br />
        <b>⇒ Response formats</b>:  the results of the REST API queries can be retrieved in three different
        formats: JSON, XML and TSV (tabular, tab-separated). By default, the REST API provides the results in
        JSON format. To retrieve results in a different format, add to your API call an HTTP header with name
        equal to <i>accept</i> and value equal to: <i>application/json</i>, <i>application/xml</i> or{' '}
        <i>application/csv</i>.<br />
        <br />
        <b>⇒ Common response structure</b>:  when using the JSON and XML response formats, all REST API
        endpoints have the following shared response fields:
      </p>
      <ul>
        <li>
          <b>status</b>: the overall status of the API call execution (<i>OK</i> in case of correctly
          retrieved results).
        </li>
        <li>
          <b>error</b>: in case of error, details on the type of error.
        </li>
        <li>
          <b>paging</b>: when using pagination, total number of results retrieved by the query, current page
          number and number of results included in the current page.
        </li>
        <li>
          <b>warnings</b>: a list of textual warning messages, if any.
        </li>
        <li>
          <b>requestpar</b>: the API call parameters provided by the user.
        </li>
        <li>
          <b>userinfo</b>: information concerning the user, including its profile.
        </li>
        <li>
          <b>payload</b>: the actual results of the API call (the format depends on the specific API call
          invoked).
        </li>
      </ul>
      <p>
        At the end of the REST API <Link to="/Tools?section=documentation">Documentation</Link>, a detailed
        description of the fields of the REST API common response structure is available (see <i>Response</i>,{' '}
        <i>ResponseError</i>, <i>Paging</i> and <i>UserInfo</i> data models).
        <br />
        <br />
        The content of the <i>payload</i> field of each API call <i>Response</i> depends on the specific API
        endpoint invoked (i.e. /gda, /vda or /dda). In particular, the{' '}
        <Link to="/Tools?section=documentation">Documentation</Link> section describes the fields of the{' '}
        <i>payloads</i> of each API endpoint (see <i>GeneDiseaseAssocDTO</i>, <i>VariantDiseaseAssocDTO</i> and{' '}
        <i>DiseaseDiseaseAssocDTO</i> data models).
        <br />
        <br />
        <b>⇒ Pagination</b>:  the endpoints of the REST API (i.e. /gda, /vda and /dda) support the pagination
        of search results. Given a specific configuration of query parameters, by relying on the pagination
        mechanism, it is possible to retrieve up to 10,000 search results (i.e. gene-disease, variant-disease
        or disease-disease associations). The number of search results present in each page is equal to 100.
        Pagination enables the access to the first 100 pages of search results (i.e. up to 10,000 search
        results): page numbers start from 0. The last page accessible is page number 99 (i.e. the 100th page).
        <br />
        <br />
        <b>⇒ Version endpoint</b>:  the REST API provides a public endpoint to obtain information on the
        current version of DISGENET data accessible by the REST API (i,e. /public/version, see the REST API{' '}
        <Link to="/interactive-console">Interactive console</Link> for more details).
      </p>
    </div>
  );
}
